import React, { useState, useEffect, useRef } from 'react';
import { buildApiUrl } from '../config';
import './DailySales.css';

const DailySales = () => {
    const [dailySales, setDailySales] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [userRole, setUserRole] = useState('');
    const [editingRecord, setEditingRecord] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState({});
    const [selectedRow, setSelectedRow] = useState(-1);
    const containerRef = useRef(null);

    useEffect(() => {
        // Get user role from session storage
        const userData = JSON.parse(sessionStorage.getItem('user'));
        console.log('User data from session storage:', userData);
        if (userData && userData.role) {
            setUserRole(userData.role);
            console.log('User role set to:', userData.role);
        } else {
            console.log('No user role found in session storage');
        }
    }, []);

    useEffect(() => {
        if (selectedDate) {
            fetchDailySales();
        }
    }, [selectedDate]);

    useEffect(() => {
        if (selectedRow < 0 || !containerRef.current) return;

        const rows = containerRef.current.querySelectorAll('tbody tr:not(.total-row)');
        if (rows[selectedRow]) {
            rows[selectedRow].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, [selectedRow]);

    const handleKeyDown = (e) => {
        if (!dailySales.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedRow((prev) => Math.min(prev + 1, dailySales.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedRow((prev) => Math.max(prev - 1, 0));
        }
    };

    const formatAmount = (value) => {
        if (value === null || value === undefined || value === '' || value === 0 || value === '0') {
            return '-';
        }
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        return Number.isInteger(num) ? String(num) : num.toFixed(2);
    };

    // UI-only: merge Oil Change / Washing / Other Cash Sales into one Cash Sales column.
    // Credit and POS amounts must not appear here.
    const getCashSalesAmount = (sale) => {
        if (parseFloat(sale.CreditSales)) return null;
        if (parseFloat(sale.PosSales)) return null;
        const cash = (parseFloat(sale.OilChange) || 0)
            + (parseFloat(sale.Washing) || 0)
            + (parseFloat(sale.OtherCashSales) || 0);
        return cash || null;
    };

    const getCreditSalesAmount = (sale) => {
        const credit = parseFloat(sale.CreditSales);
        return credit || null;
    };

    const getPosSalesAmount = (sale) => {
        const pos = parseFloat(sale.PosSales);
        return pos || null;
    };

    const isPosRecovery = (sale) => {
        const method = String(sale.RecoveryPaymentMethod || '').toLowerCase();
        return Boolean(parseFloat(sale.Recovery)) && method === 'pos';
    };

    const formatPaymentMethodLabel = (method) => {
        if (!method) return '-';
        const m = String(method).toLowerCase();
        if (m === 'pos') return 'POS';
        if (m === 'bank-transfer') return 'Bank Transfer';
        return m.charAt(0).toUpperCase() + m.slice(1);
    };

    const fetchDailySales = async () => {
        try {
            const response = await fetch(buildApiUrl(`api/dailySales?date=${selectedDate}`));
            const data = await response.json();
            if (data.success) {
                console.log('Daily Sales Data:', data.data); // Debug log
                setDailySales(data.data);
                setSelectedRow(-1);
            } else {
                console.error('Error fetching daily sales data:', data.error);
            }
        } catch (err) {
            console.error('Error fetching daily sales data:', err);
        }
    };

    // Display-only: formats dates as DD-Mmm-YYYY (e.g. 26-Jun-2026). Does not affect API or date picker.
    const formatDate = (dateString) => {
        if (!dateString || String(dateString).toLowerCase() === 'null') return '-';

        const monthAbbr = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const s = String(dateString).trim();
        let day;
        let month;
        let year;

        let match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            year = +match[1];
            month = +match[2];
            day = +match[3];
        } else if ((match = s.match(/^(\d{2})-(\d{2})-(\d{4})$/))) {
            day = +match[1];
            month = +match[2];
            year = +match[3];
        } else if ((match = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/))) {
            day = +match[1];
            month = +match[2];
            year = +match[3];
        } else if ((match = s.match(/^(\d{1,2})-([a-zA-Z]{3})-(\d{4})$/))) {
            const monthIndex = monthAbbr.indexOf(match[2].toLowerCase());
            if (monthIndex >= 0) {
                return `${String(match[1]).padStart(2, '0')}-${monthAbbr[monthIndex]}-${match[3]}`;
            }
            return s;
        } else {
            return 'Invalid Date';
        }

        if (month < 1 || month > 12 || day < 1 || day > 31) return 'Invalid Date';
        return `${String(day).padStart(2, '0')}-${monthAbbr[month - 1]}-${year}`;
    };


    const totals = dailySales.reduce(
        (acc, sale) => {
            acc.OilChange += parseFloat(sale.OilChange) || 0;
            acc.Washing += parseFloat(sale.Washing) || 0;
            acc.OtherCashSales += parseFloat(sale.OtherCashSales) || 0;
            const recoveryAmt = parseFloat(sale.Recovery) || 0;
            acc.Recovery += recoveryAmt;
            if (recoveryAmt) {
                if (isPosRecovery(sale)) acc.PosRecovery += recoveryAmt;
                else acc.CashRecovery += recoveryAmt;
                // Blank method is treated as cash for older rows
                const recoveryMethod = String(sale.RecoveryPaymentMethod || 'cash').toLowerCase();
                if (recoveryMethod === 'cash') acc.CashOnlyRecovery += recoveryAmt;
            }
            acc.CreditSales += parseFloat(sale.CreditSales) || 0;
            acc.PosSales += parseFloat(sale.PosSales) || 0;
            acc.Commission -= parseFloat(sale.Commission) || 0;
            const rowCashSales = getCashSalesAmount(sale);
            if (rowCashSales) acc.CashSales += rowCashSales;
            // Cash-only washing / oil for summary breakdown (exclude credit & POS rows)
            if (!parseFloat(sale.CreditSales) && !parseFloat(sale.PosSales)) {
                acc.CashWashing += parseFloat(sale.Washing) || 0;
                acc.CashOil += parseFloat(sale.OilChange) || 0;
            }
            // Include regular expenses and settlements, but exclude overtime payments from expense calculation
            // Overtime payments are identified by having OvertimeID AND Service starting with "Overtime:"
            if (sale.Expense && !(sale.OvertimeID && sale.Service?.startsWith('Overtime:'))) {
                acc.Expense += parseFloat(sale.Expense) || 0;
            }
            acc.StockCreditAmount += parseFloat(sale.StockCreditAmount) || 0;
            acc.Balance = sale.Balance || acc.Balance;
            return acc;
        },
        { OilChange: 0, Washing: 0, OtherCashSales: 0, CashSales: 0, CashWashing: 0, CashOil: 0, Recovery: 0, CashRecovery: 0, CashOnlyRecovery: 0, PosRecovery: 0, CreditSales: 0, PosSales: 0, Commission: 0, Expense: 0, StockCreditAmount: 0, Balance: 0 }
    );

    // --- Summary totals (fields match classic Summary image; enhanced UI kept) ---
    // Cash / Credit / POS stay as aggregated from table columns (cash-only, credit-only, POS-only).
    // Recovery = ALL recovery methods (cash + POS + etc.) — shown only under Recovery, not elsewhere.

    // Total sale amount = cash + credit + POS
    totals.totalSaleAmount = totals.CashSales + totals.CreditSales + totals.PosSales;

    // Balance = total sale − expense − commission + all recovery
    totals.summaryBalance = totals.totalSaleAmount - totals.Expense - Math.abs(totals.Commission) + totals.Recovery;

    // Cash Received = cash sales + recovery received in cash
    totals.summaryCashReceived = totals.CashSales + totals.CashOnlyRecovery;

    // Previous versions:
    // totals.summaryBalance = totals.totalSaleAmount - totals.Expense - Math.abs(totals.Commission) - totals.Recovery;
    // totals.summaryCashReceived = totals.CashSales - totals.Expense + totals.CashOnlyRecovery;
    // totals.summaryBalance = totals.totalSaleAmount - totals.Expense - Math.abs(totals.Commission);
    // totals.summaryCashReceived = totals.CashSales - totals.Expense;

    // --- Kept for future use (previous formulas) ---
    // totals.cashReceived = totals.CashSales + totals.CashRecovery; // excluded POS recovery
    // totals.cashReceivables = totals.cashReceived - totals.Expense - Math.abs(totals.Commission);
    // totals.totalSaleAmount = totals.CashSales + totals.CreditSales; // POS excluded
    // totals.posCombined = totals.PosSales + totals.PosRecovery;
    // totals.summaryBalance = totals.totalSaleAmount - totals.Expense - Math.abs(totals.Commission);


    // Export currently displayed Daily Sales to CSV
    const exportDailySalesCSV = () => {
        if (!dailySales || dailySales.length === 0) {
            alert('No data to export');
            return;
        }

        const headers = [
            'Sr',
            'Service',
            'Vehicle Number',
            'Vehicle Type',
            'Driver',
            'Company Name',
            'Cash Sales',
            'Recovery',
            'Recovery Method',
            'Credit Sales',
            'POS',
            'Commission',
            'Expense',
            'OT',
            'Stock Credit',
            'Remarks',
            'Balance'
        ];

        const rows = dailySales.map((sale, idx) => [
            String(idx + 1),
            sale.Service ?? '-',
            sale.VehicleNumber ?? '-',
            sale.VehicleType ?? '-',
            sale.Driver ?? '-',
            sale.CompanyName ?? '-',
            getCashSalesAmount(sale) ?? '-',
            sale.Recovery ?? '-',
            sale.Recovery ? formatPaymentMethodLabel(sale.RecoveryPaymentMethod) : '-',
            getCreditSalesAmount(sale) ?? '-',
            getPosSalesAmount(sale) ?? '-',
            sale.Commission ?? '-',
            sale.Expense ?? '-',
            sale.OvertimeAmount ?? '-',
            sale.StockCreditAmount ?? '-',
            sale.Remarks ?? '-',
            sale.Balance ?? '-',
        ]);

        // Add totals row at the end
        rows.push([
            'Total', '', '', '', '', '',
            totals.CashSales.toFixed(2),
            totals.Recovery.toFixed(2),
            '',
            totals.CreditSales.toFixed(2),
            totals.PosSales.toFixed(2),
            Math.abs(totals.Commission).toFixed(2),
            totals.Expense.toFixed(2),
            '',
            totals.StockCreditAmount.toFixed(2),
            '',
            String(totals.Balance),
        ]);

        const csv = [headers, ...rows]
            .map((row) => row
                .map((cell) => {
                    const val = cell === null || cell === undefined ? '' : String(cell);
                    // Escape quotes and wrap in quotes if contains comma or newline
                    const escaped = val.replace(/"/g, '""');
                    return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
                })
                .join(','))
            .join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileDate = selectedDate || new Date().toISOString().split('T')[0];
        link.download = `Daily_Sales_${fileDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleEdit = (sale) => {
        setEditingRecord(sale);
        setEditFormData({
            // Common fields
            servicePrice: sale.OilChange || sale.Washing || sale.CreditSales || sale.Expense || sale.Recovery,
            notes: sale.Remarks,
            // Sales specific
            paymentMethod: sale.payment_method || (sale.CreditSales ? 'credit' : 'cash'), // Set default based on CreditSales
            commission: sale.Commission,
            // Expense specific
            expenseAmount: sale.Expense,
            expenseType: sale.Service?.replace('Expense: ', ''),
            expenseDescription: sale.Remarks,
            // Payment specific
            paymentAmount: sale.Recovery,
            paymentNotes: sale.Remarks
        });
        setShowEditModal(true);
    };

    const handleEditSubmit = async () => {
        if (!window.confirm('Are you sure you want to update this record?')) {
            return;
        }

        try {
            const response = await fetch(buildApiUrl('api/dailySales/edit'), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    saleId: editingRecord.SaleID,
                    expenseId: editingRecord.ExpenseID,
                    paymentId: editingRecord.PaymentID,
                    ...editFormData
                })
            });

            const data = await response.json();

            if (data.success) {
                // Refresh the data
                fetchDailySales();
                setShowEditModal(false);
                alert('Record updated successfully');
            } else {
                alert('Failed to update record: ' + data.error);
            }
        } catch (error) {
            console.error('Error updating record:', error);
            alert('Error updating record. Please try again.');
        }
    };

    const renderEditForm = () => {
        if (!editingRecord) return null;

        return (
            <div className="edit-modal">
                <div className="edit-modal-content">
                    <h2>Edit Record</h2>
                    {editingRecord.SaleID && (
                        <>
                            <div className="form-group">
                                <label>Service Price:</label>
                                <input
                                    type="number"
                                    value={editFormData.servicePrice || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, servicePrice: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Payment Method:</label>
                                <input
                                    type="text"
                                    value={editFormData.paymentMethod || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, paymentMethod: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Commission:</label>
                                <input
                                    type="number"
                                    value={editFormData.commission || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, commission: e.target.value })}
                                />
                            </div>
                        </>
                    )}
                    {editingRecord.ExpenseID && (
                        <>
                            <div className="form-group">
                                <label>Expense Amount:</label>
                                <input
                                    type="number"
                                    value={editFormData.expenseAmount || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, expenseAmount: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Expense Type:</label>
                                <input
                                    type="text"
                                    value={editFormData.expenseType || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, expenseType: e.target.value })}
                                />
                            </div>
                        </>
                    )}
                    {editingRecord.PaymentID && (
                        <>
                            <div className="form-group">
                                <label>Payment Amount:</label>
                                <input
                                    type="number"
                                    value={editFormData.paymentAmount || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, paymentAmount: e.target.value })}
                                />
                            </div>
                        </>
                    )}
                    <div className="form-group">
                        <label>Notes:</label>
                        <input
                            type="text"
                            value={editFormData.notes || ''}
                            onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                        />
                    </div>
                    <div className="modal-buttons">
                        <button type="button" className="save-btn" onClick={handleEditSubmit}>Save Changes</button>
                        <button type="button" className="cancel-btn" onClick={() => setShowEditModal(false)}>Cancel</button>
                    </div>
                </div>
            </div>
        );
    };

    const handleDelete = async (sale) => {
        // Show confirmation dialog
        if (!window.confirm('Are you sure you want to delete this record?')) {
            return;
        }

        try {
            const response = await fetch(buildApiUrl('api/dailySales/delete'), {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    saleId: sale.SaleID,
                    expenseId: sale.ExpenseID,
                    paymentId: sale.PaymentID,
                    overtimeId: sale.OvertimeID,
                    settlementId: sale.SettlementID,
                    stockTransactionId: sale.StockTransactionID
                })
            });

            const data = await response.json();

            if (data.success) {
                // Remove the deleted record from the state
                setDailySales(prevSales => prevSales.filter(item => {
                    if (sale.SaleID) return item.SaleID !== sale.SaleID;
                    if (sale.ExpenseID) return item.ExpenseID !== sale.ExpenseID;
                    if (sale.PaymentID) return item.PaymentID !== sale.PaymentID;
                    if (sale.OvertimeID) return item.OvertimeID !== sale.OvertimeID;
                    if (sale.SettlementID) return item.SettlementID !== sale.SettlementID;
                    if (sale.StockTransactionID) return item.StockTransactionID !== sale.StockTransactionID;
                    return true;
                }));

                alert('Record deleted successfully');
            } else {
                alert('Failed to delete record: ' + data.error);
            }
        } catch (error) {
            console.error('Error deleting record:', error);
            alert('Error deleting record. Please try again.');
        }
    };

    return (
        <div className="daily-sales-container">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                <h1 style={{ margin: 0 }}>Daily Sales</h1>
                <button
                    onClick={exportDailySalesCSV}
                    style={{
                        background: 'none',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#217346',
                        padding: '6px 10px',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                    title="Export current table to CSV"
                >
                    📊
                </button>
            </div>
            <div className="filter-container">
                <label htmlFor="date-filter">Select Date:</label>
                <input
                    type="date"
                    id="date-filter"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                />
            </div>
            <div
                className="table-container"
                tabIndex={0}
                ref={containerRef}
                onKeyDown={handleKeyDown}
            >
                <table className="daily-sales-table">
                    <thead>
                        <tr>
                            <th>Sr</th>
                            <th>Service</th>
                            <th>Vehicle Number</th>
                            <th>Vehicle Type</th>
                            {/* <th>Driver</th> */}
                            <th className="company-name-header">Company Name</th>
                            <th>Cash Sales</th>
                            <th>Recovery</th>
                            <th>Recovery Method</th>
                            <th>Credit Sales</th>
                            <th>POS</th>
                            <th>Commission</th>
                            <th>Expense</th>
                            <th>OT</th>
                            <th>Stock Credit</th>
                            <th>Remarks</th>
                            <th>Balance</th>
                            {userRole === 'manager' && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {dailySales.length > 0 ? (
                            dailySales.map((sale, index) => (
                                sale.Service !== null ? (
                                    <tr
                                        key={index}
                                        onClick={() => setSelectedRow(index)}
                                        className={index === selectedRow ? 'highlighted-row' : ''}
                                    >
                                        <td>{index + 1}</td>
                                        <td>{sale.Service}</td>
                                        <td>{sale.VehicleNumber}</td>
                                        <td>{sale.VehicleType}</td>
                                        {/* <td>{sale.Driver}</td> */}
                                        <td className="company-name-cell">{sale.CompanyName}</td>
                                        <td>{formatAmount(getCashSalesAmount(sale))}</td>
                                        <td>{formatAmount(sale.Recovery)}</td>
                                        <td>{sale.Recovery ? formatPaymentMethodLabel(sale.RecoveryPaymentMethod) : '-'}</td>
                                        <td>{formatAmount(getCreditSalesAmount(sale))}</td>
                                        <td>{formatAmount(getPosSalesAmount(sale))}</td>
                                        <td>{formatAmount(sale.Commission)}</td>
                                        <td>{formatAmount(sale.Expense)}</td>
                                        <td>{formatAmount(sale.OvertimeAmount)}</td>
                                        <td>{formatAmount(sale.StockCreditAmount)}</td>
                                        <td>{sale.Remarks}</td>
                                        <td>{formatAmount(sale.Balance)}</td>
                                        {userRole === 'manager' && (
                                            <td className="actions-cell">
                                                <button
                                                    className="edit-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEdit(sale);
                                                    }}
                                                    title="Edit"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    className="delete-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(sale);
                                                    }}
                                                    title="Delete"
                                                >
                                                    🗑️
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ) : (
                                    // Separate row for expenses
                                    <tr
                                        key={`expense-${index}`}
                                        className={`expense-row${index === selectedRow ? ' highlighted-row' : ''}`}
                                        onClick={() => setSelectedRow(index)}
                                    >
                                        <td colSpan="5"><strong>Expense</strong></td>
                                        <td>-</td>
                                        <td>-</td>
                                        <td>-</td>
                                        <td>-</td>
                                        <td>-</td>
                                        <td>-</td>
                                        <td><strong>{formatAmount(sale.Expense)}</strong></td>
                                        <td>-</td>
                                        <td>{formatAmount(sale.StockCreditAmount)}</td>
                                        <td>{sale.Remarks || '-'}</td>
                                        <td>-</td>
                                        {userRole === 'manager' && <td></td>}
                                    </tr>
                                )
                            ))
                        ) : (
                            <tr>
                                <td colSpan={userRole === 'manager' ? 17 : 16}>No data available for the selected date.</td>
                            </tr>
                        )}
                        {/* Add total row */}
                        {dailySales.length > 0 && (
                            <tr className="total-row">
                                <td colSpan="5"><strong>Total</strong></td>
                                <td><strong>{totals.CashSales > 0 ? totals.CashSales.toFixed(2) : '-'}</strong></td>
                                <td><strong>{totals.Recovery > 0 ? totals.Recovery.toFixed(2) : '-'}</strong></td>
                                <td>-</td>
                                <td><strong>{totals.CreditSales > 0 ? totals.CreditSales.toFixed(2) : '-'}</strong></td>
                                <td><strong>{totals.PosSales > 0 ? totals.PosSales.toFixed(2) : '-'}</strong></td>
                                <td><strong>{totals.Commission !== 0 ? Math.abs(totals.Commission).toFixed(2) : '-'}</strong></td>
                                <td><strong>{totals.Expense > 0 ? totals.Expense.toFixed(2) : '-'}</strong></td>
                                <td>-</td>
                                <td><strong>{totals.StockCreditAmount > 0 ? totals.StockCreditAmount.toFixed(2) : '-'}</strong></td>
                                <td>-</td>
                                <td><strong>{totals.Balance !== 0 ? totals.Balance : '-'}</strong></td>
                                {userRole === 'manager' && <td>-</td>}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Daily Summary Report — centered card matching mockup; POS is the only new data line */}
            <div className="daily-summary-wrap">
                <div className="daily-summary-report">
                    <div className="daily-summary-header">
                        <div className="daily-summary-title">
                            <span className="daily-summary-icon" aria-hidden="true">
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    <rect x="9" y="3" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </span>
                            <h2>Daily Summary Report</h2>
                        </div>
                        <div className="daily-summary-date">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                                <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            {selectedDate ? formatDate(selectedDate) : '-'}
                        </div>
                    </div>

                    <div className="daily-summary-body">
                        <div className="daily-summary-section-label">SALES</div>

                        {/* --- Previous breakdown rows (kept for future) ---
                        <div className="daily-summary-row row-blue">
                            <span className="daily-summary-label">
                                <span className="row-ico" aria-hidden="true">💧</span>
                                VEH WASHING SALE
                            </span>
                            <span>{Number((totals.CashWashing || 0) + (totals.OtherCashSales || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED</span>
                        </div>
                        <div className="daily-summary-row row-blue">
                            <span className="daily-summary-label">
                                <span className="row-ico" aria-hidden="true">🛢️</span>
                                OIL + FILTER CHANGE
                            </span>
                            <span>{Number((totals.CashOil || 0) + (totals.StockCreditAmount || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED</span>
                        </div>
                        --- */}

                        <div className="daily-summary-row row-blue">
                            <span className="daily-summary-label">
                                <span className="row-ico" aria-hidden="true">💵</span>
                                Cash Sales
                            </span>
                            <span>{Number(totals.CashSales || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED</span>
                        </div>
                        <div className="daily-summary-row row-gray">
                            <span className="daily-summary-label">
                                <span className="row-ico" aria-hidden="true">💳</span>
                                Credit Sales
                            </span>
                            <span>{Number(totals.CreditSales || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED</span>
                        </div>
                        <div className="daily-summary-row row-blue">
                            <span className="daily-summary-label">
                                <span className="row-ico" aria-hidden="true">🏧</span>
                                POS
                            </span>
                            <span>{Number(totals.PosSales || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED</span>
                        </div>
                        <div className="daily-summary-row highlight-teal">
                            <span className="daily-summary-label">
                                <span className="row-ico" aria-hidden="true">📈</span>
                                TOTAL SALE AMOUNT
                            </span>
                            <span>{Number(totals.totalSaleAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED</span>
                        </div>

                        <div className="daily-summary-section-label">DEDUCTIONS</div>

                        <div className="daily-summary-row row-gray">
                            <span className="daily-summary-label">
                                <span className="row-ico" aria-hidden="true">📄</span>
                                LESS EXPENDITURES
                            </span>
                            <span className="neg">-{Number(totals.Expense || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED</span>
                        </div>
                        <div className="daily-summary-row row-gray">
                            <span className="daily-summary-label">
                                <span className="row-ico" aria-hidden="true">👤</span>
                                LESS COMMISSION
                            </span>
                            <span className="neg">-{Number(Math.abs(totals.Commission || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED</span>
                        </div>
                        <div className="daily-summary-row highlight-green">
                            <span className="daily-summary-label">
                                <span className="row-ico" aria-hidden="true">💵</span>
                                Recovery
                            </span>
                            <span>{Number(totals.Recovery || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED</span>
                        </div>
                        <div className="daily-summary-row balance-row">
                            <span className="daily-summary-label">
                                <span className="row-ico" aria-hidden="true">👛</span>
                                Balance
                            </span>
                            <span>{Number(totals.summaryBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED</span>
                        </div>

                        <div className="daily-summary-section-label">PAYMENT RECEIVED</div>

                        {/* --- Previous POS payment line (kept for future) ---
                        <div className="daily-summary-row highlight-green">
                            <span className="daily-summary-label">
                                <span className="row-ico" aria-hidden="true">🏦</span>
                                Direct Bank Transfer (POS)
                            </span>
                            <span>{Number(totals.posCombined || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED</span>
                        </div>
                        --- */}

                        <div className="daily-summary-row highlight-green">
                            <span className="daily-summary-label">
                                <span className="row-ico" aria-hidden="true">💶</span>
                                Cash Received
                            </span>
                            <span>{Number(totals.summaryCashReceived || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED</span>
                        </div>

                        <div className="daily-summary-footer">
                            <span>• All amounts in AED • Totals net of deductions</span>
                            <span>Generated | {selectedDate ? formatDate(selectedDate) : '-'}</span>
                        </div>
                    </div>
                </div>
            </div>
            {showEditModal && renderEditForm()}
        </div>
    );

};

export default DailySales;
